// Layer 4 (UI) — Process › Loop status (locked comp `process.html` A): the
// loop table beside the selected loop's exception and the control room's ask,
// an 8/4 row. The AIC5002 row carries its variable (AT-150 with its 24 h
// control strip and the ZN-REC chip, B's read). Where the table would fall
// below about 720 px (1280 wide), the table takes the full width and the
// exception row drops under it (C's fallback). Renders Scene.transform.ts.

import { Fragment } from 'react';
import { Commentable } from '@/shared/comments/Commentable';
import { Table, TBody, TD, TH, THead, TR } from '@/design-system';
import { cn } from '@/shared/cn';
import { ActorChip } from '../advisory/PipelineStrip';
import { CARD_TABLE, HOT_ROW } from '../downtime/DowntimeCard';
import { CaseCodeChip } from '../quality/QualityUi';
import { ControlStrip } from './ControlStrip';
import { AnswerButton, ProcessCard, ProcessChartBlock, SubLabel, ToneChip } from './ProcessUi';
import {
  LOOP_STATE_QUESTION,
  loopEvidenceAnsweredLabel,
  type LoopEvidenceItem,
  type LoopException,
  type LoopRow,
} from './Scene.transform';

type OpenCase = (caseId: string, title: string) => void;

/** The selected row: the selection wash plus a 3px crimson rule on its first cell. */
const SELECTED_ROW = cn(HOT_ROW, '[&>td:first-child]:shadow-[inset_3px_0_0_var(--color-accent-500)]');

export function LoopStatusTab({
  rows,
  selectedId,
  onSelect,
  exception,
  evidence,
  canAnswer,
  answeredAtLabel,
  onAnswer,
  onOpenCase,
}: {
  rows: readonly LoopRow[];
  selectedId: string;
  onSelect: (id: string) => void;
  exception: LoopException;
  evidence: readonly LoopEvidenceItem[];
  canAnswer: boolean;
  answeredAtLabel: (item: LoopEvidenceItem) => string;
  onAnswer: (evidenceId: string, answer: 'AUTO' | 'MANUAL') => void;
  onOpenCase: OpenCase;
}) {
  const selected = rows.find((row) => row.id === selectedId) ?? rows[0];
  return (
    <Commentable name="LoopStatus">
      <div className="grid min-w-0 gap-3 @[1040px]:grid-cols-12 @[1040px]:items-start" data-loop-status>
        <ProcessCard title="Control loops" meta="mode · time in mode" className="@[1040px]:col-span-8" data-loop-table>
          <div className={cn(CARD_TABLE, '[&_td]:align-middle')}>
            <Table wrap={false} className="tabular-nums">
              <THead>
                <TR>
                  <TH>Loop</TH>
                  <TH>Mode</TH>
                  <TH>Since · changed by</TH>
                  <TH>Process variable</TH>
                  <TH>CRO note</TH>
                </TR>
              </THead>
              <TBody>
                {rows.map((row) => (
                  <TR
                    key={row.id}
                    data-loop-row={row.tag}
                    aria-selected={row.id === selected?.id}
                    onRowClick={() => onSelect(row.id)}
                    className={row.id === selected?.id ? SELECTED_ROW : undefined}
                  >
                    <TD className="min-w-[8.5rem]">
                      <span className="block font-semibold leading-snug !text-ink-primary">{row.name}</span>
                      <span className="block text-xs leading-snug text-muted">{row.tagLine}</span>
                    </TD>
                    <TD className="whitespace-nowrap">
                      <ToneChip tone={row.tone} data-mode-tone={row.tone}>{row.modeLabel}</ToneChip>
                    </TD>
                    <TD className="whitespace-nowrap">
                      <span className="inline-flex items-center gap-2">
                        <span className="text-ink-secondary">{row.since ?? '—'}</span>
                        {row.changedBy ? <ActorChip actor="Human">{row.changedBy}</ActorChip> : null}
                      </span>
                    </TD>
                    <TD className="whitespace-nowrap" data-loop-pv={row.tag}>
                      {row.pv ? (
                        <span className="flex flex-col gap-0.5">
                          <span className="flex items-baseline gap-1.5">
                            <span className="text-sm font-semibold text-ink-primary">
                              {row.pv.value} <span className="text-xs font-medium text-muted">{row.pv.unit}</span>
                            </span>
                            <span className="text-xs text-muted">{row.pv.tag} · {row.pv.at}</span>
                          </span>
                          <ControlStrip chart={row.pv.chart} />
                          <span className={cn('text-xs', row.pv.chart.beyond ? 'font-semibold text-[var(--state-blocked)]' : 'text-muted')}>
                            {row.pv.chart.stateLabel}
                          </span>
                          {row.ask ? (
                            <span className="mt-0.5 inline-flex items-center gap-1.5">
                              <CaseCodeChip code={row.ask.caseCode} label={row.ask.caseTitle} onOpen={() => onOpenCase(row.ask!.caseId, row.ask!.caseTitle)} />
                              <span className="text-xs text-muted">asks</span>
                            </span>
                          ) : null}
                        </span>
                      ) : null}
                    </TD>
                    <TD className="min-w-[11rem] text-xs leading-snug text-muted">{row.note}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </div>
        </ProcessCard>

        <div className="grid min-w-0 gap-3 @[760px]:grid-cols-12 @[760px]:items-start @[1040px]:col-span-4 @[1040px]:grid-cols-1">
          {selected ? (
            <ExceptionCard row={selected} exception={exception} onOpenCase={onOpenCase} className="@[760px]:col-span-8 @[1040px]:col-span-1" />
          ) : null}
          {evidence.length > 0 ? (
            <AskCard
              evidence={evidence}
              canAnswer={canAnswer}
              answeredAtLabel={answeredAtLabel}
              onAnswer={onAnswer}
              onOpenCase={onOpenCase}
              className="@[760px]:col-span-4 @[1040px]:col-span-1"
            />
          ) : null}
        </div>
      </div>
    </Commentable>
  );
}

function ExceptionCard({
  row,
  exception,
  onOpenCase,
  className,
}: {
  row: LoopRow;
  exception: LoopException;
  onOpenCase: OpenCase;
  className?: string;
}) {
  const { rule, caseRef } = exception;
  const showChart = row.pv?.chart.beyond;
  return (
    <ProcessCard
      title={`Exception · ${row.tag}`}
      className={className}
      data-loop-exception={row.tag}
      actions={
        <span className="flex items-center gap-1.5">
          <ToneChip tone={row.tone}>{row.modeLabel}</ToneChip>
          {caseRef ? <CaseCodeChip code={caseRef.code} label={caseRef.title} onOpen={() => onOpenCase(caseRef.caseId, caseRef.title)} /> : null}
        </span>
      }
    >
      {showChart && row.pv ? (
        <div className="mb-3 border-b border-[var(--hairline)] pb-3">
          <ProcessChartBlock
            chart={row.pv.chart}
            title={
              <span className="flex items-baseline gap-1.5 text-xs font-medium text-ink-primary">
                {row.pv.readingName}
                <span className="text-xs font-normal text-muted">{row.pv.tag}</span>
              </span>
            }
          />
        </div>
      ) : null}
      {rule ? (
        <div data-matched-rule>
          <SubLabel>Matched rule</SubLabel>
          <p className="text-sm font-semibold leading-snug text-ink-primary">{rule.diagnosis}</p>
          <ul className="mt-1.5 divide-y divide-[var(--color-divider)]" data-rule-conditions>
            {rule.conditions.map((condition) => (
              <li
                key={condition.text}
                data-condition={condition.owned ? (condition.met ? 'met' : 'not-met') : 'unread'}
                className="grid grid-cols-[1rem_minmax(0,1fr)_auto] items-baseline gap-2 py-1.5 text-sm"
              >
                <span
                  aria-hidden
                  className={cn(
                    'text-center font-semibold',
                    !condition.owned && 'text-muted',
                    condition.owned && condition.met && 'text-[var(--state-online)]',
                    condition.owned && !condition.met && 'text-[var(--state-warning)]',
                  )}
                >
                  {condition.owned ? (condition.met ? '✓' : '✕') : '·'}
                </span>
                <span className={condition.owned ? 'text-ink-primary' : 'text-ink-secondary'}>{condition.text}</span>
                {condition.status ? (
                  <span
                    className={cn(
                      'whitespace-nowrap text-xs',
                      condition.met ? 'text-muted' : 'font-semibold text-[var(--state-warning)]',
                    )}
                  >
                    {condition.status}
                  </span>
                ) : <span />}
              </li>
            ))}
          </ul>
          <div className="mt-2 border-t border-[var(--hairline)] pt-2.5">
            <SubLabel end={<ActorChip actor="Human">{rule.actionActor}</ActorChip>}>Action</SubLabel>
            <p className="text-sm leading-snug text-ink-primary">{rule.action}</p>
          </div>
        </div>
      ) : (
        <p className="text-xs text-muted">No matched diagnostic rule for this loop tag.</p>
      )}
    </ProcessCard>
  );
}

function AskCard({
  evidence,
  canAnswer,
  answeredAtLabel,
  onAnswer,
  onOpenCase,
  className,
}: {
  evidence: readonly LoopEvidenceItem[];
  canAnswer: boolean;
  answeredAtLabel: (item: LoopEvidenceItem) => string;
  onAnswer: (evidenceId: string, answer: 'AUTO' | 'MANUAL') => void;
  onOpenCase: OpenCase;
  className?: string;
}) {
  const first = evidence[0]!;
  return (
    <ProcessCard
      title={canAnswer ? 'Evidence requested of you' : `Asked of the ${first.targetLabel.toLowerCase()}`}
      className={className}
      data-loop-ask
      actions={<CaseCodeChip code={first.caseCode} label={first.caseTitle} onOpen={() => onOpenCase(first.caseId, first.caseTitle)} />}
    >
      <div className="divide-y divide-[var(--color-divider)]">
        {evidence.map((item) => (
          <Fragment key={item.evidenceId}>
            <div className="flex flex-col gap-2 py-1 first:pt-0 last:pb-0" data-evidence={item.evidenceId} data-evidence-status={item.status}>
              <p className="text-sm text-ink-primary">
                {item.caseCode} asks: {item.text}
              </p>
              {item.status === 'open' ? (
                canAnswer ? (
                  <div className="flex flex-wrap gap-2" role="group" aria-label={LOOP_STATE_QUESTION}>
                    <AnswerButton primary onClick={() => onAnswer(item.evidenceId, 'AUTO')}>Confirm AUTO</AnswerButton>
                    <AnswerButton onClick={() => onAnswer(item.evidenceId, 'MANUAL')}>Report MANUAL</AnswerButton>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-2">
                    <ToneChip tone="neutral">Open</ToneChip>
                    <ActorChip actor="Human">{item.targetLabel}</ActorChip>
                  </div>
                )
              ) : item.answer ? (
                <div className="flex flex-wrap items-center gap-2">
                  <ToneChip tone="ok" data-evidence-answer={item.answer}>{loopEvidenceAnsweredLabel(item.answer)}</ToneChip>
                  <span className="text-xs tabular-nums text-muted">{item.targetLabel} · {answeredAtLabel(item)}</span>
                </div>
              ) : null}
            </div>
          </Fragment>
        ))}
      </div>
    </ProcessCard>
  );
}
